/**
 * Base theme preset
 *
 * The default foundation theme for all providers. Uses neutral colors
 * that providers override with their detected brand colors.
 *
 * This is the RECOMMENDED starting point for new providers. Instead of
 * choosing from multiple presets (warm, minimal, professional, clinical),
 * providers should use the base theme and override specific colors via
 * the `/detect-design-system` skill.
 *
 * @see docs/engineering/2026-01-07-deprecation.md for preset deprecation plan
 */

import { ThemeSchema, type ThemeDensity, type Theme } from '../types';

/**
 * The default density block, shared by `base` and every legacy colour preset
 * (`clinical`, `warm`, `professional`, `minimal`, `wellness`). HAL-495.
 *
 * Exported rather than copy-pasted so the six presets that want identical
 * layout rhythm cannot drift apart. The editorial and cosmetic-atelier presets
 * deliberately do NOT spread this — their rails, gutters and section gaps are
 * Charter-specified and differ on purpose; see each preset's DESIGN_SYSTEM.md.
 *
 * Before this existed, `themeToCss()` emitted no `--density-*` variables for
 * any provider on these presets — which is every real provider — so the
 * measure tokens were unreachable and every prose block fell back to a
 * hardcoded `max-w-3xl` (768px, measured at up to 114 characters per line).
 *
 * VALUE CHOICE, deliberate: every key that an already-shipping shared
 * component reads is set to that component's EXISTING fallback, so adopting
 * this block changes nothing that was previously rendering:
 *   - `contentMaxWidthEditorial: 80rem` = `Container.tsx`'s hardcoded fallback
 *   - `gutterDesktop: 4rem`             = `ContactFormSection.tsx`'s fallback
 * The remaining keys have no shared (non-cosmetic-atelier) consumer today, so
 * their values are inert here; they are set to sane defaults rather than
 * sentinels so a future shared consumer inherits something sensible.
 *
 * If you add a shared component that reads a `--density-*` var, check its
 * fallback against this block before shipping — a mismatch silently restyles
 * every provider on one of these six presets.
 */
export const DEFAULT_DENSITY: ThemeDensity = {
  homeSectionGap: '4rem',
  homeSectionGapDesktop: '6rem',
  homeMaxSections: 11,
  // The measure trio. See `ThemeDensitySchema`'s docblock for why these are
  // expressed in `ch` and why display is the smallest of the three.
  contentMaxWidthDisplay: '22ch',
  contentMaxWidthLede: '48ch',
  contentMaxWidthProse: '68ch',
  contentMaxWidthEditorial: '80rem',
  contentMaxWidthFullBleed: '100vw',
  gutterMobile: '1.5rem',
  gutterDesktop: '4rem',
};

export const baseTheme: Theme = ThemeSchema.parse({
  name: 'base',
  colors: {
    // Neutral foundation - designed to be overridden
    primary: '#1a1a1a',
    primaryDark: '#000000',
    primaryLight: '#333333',
    accent: '#0066cc',
    accentDark: '#004d99',
    accentLight: '#3399ff',
    background: '#ffffff',
    backgroundAlt: '#f5f5f5',
    surface: '#ffffff',
    text: '#1a1a1a',
    textMuted: '#666666',
    textInverse: '#ffffff',
    border: '#e0e0e0',
    success: '#16a34a',
    warning: '#a16207',
    error: '#ef4444',
    // Medical-specific (consistent across all themes)
    // HAL-33: was #0E8A5F, which is 4.36:1 on white. GroupedSourceList renders
    // these as a 12px h4, so they are normal text and owe 4.5:1 -- it failed for
    // every provider on this preset, not just the one whose audit caught it.
    // #0C7A53 is 5.35:1. The literal is duplicated as a CSS-var fallback in
    // GroupedSourceList, SourceCitation and wp-utils (which has no var to fall
    // back on at all); move them together or the fallback quietly restores the
    // failing colour wherever the var does not resolve. Pinned by
    // presets/contrast.spec.ts.
    evidenceStrong: '#0C7A53',
    evidenceModerate: '#B45309',
    evidenceLow: '#6b7280',
    safetyWarning: '#ef4444',
    safetyInfo: '#3b82f6',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyHeading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeightHeading: '700',
    fontSizeBase: '1rem',
    fontSizeSm: '0.875rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '1.875rem',
    fontSize4xl: '2.25rem',
    lineHeightBase: '1.5',
    lineHeightTight: '1.25',
    lineHeightLoose: '1.75',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  },
  density: DEFAULT_DENSITY,
});
